import { Modal, Col, Row, Card } from "react-bootstrap";
import { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getSocket } from '../../../../socket';
import { getTimerColors } from 'utils/timerColors';
import { getActivityTimingLimits } from 'utils/activityTiming';
import PathwayActivity from './PathwayActivity';

import Role from './RegularComponents/Role';
import Limits from './SharedComponents/Limits';
import Event from './RegularComponents/Event';
import Presenter from './RegularComponents/Presenter';
import Bell from './RegularComponents/Bell';
import Clock from './RegularComponents/Clock';
import Timer from "./RegularComponents/Timer";

export default function RegularItem({ item, getMeeting, meeting, isPlaying, setIsPlaying, showClockText = true, timerDisplayEnabled = true, allowTimerControls = true, timerControlsTarget = null, remoteControlled = false, swipeHandlers }) {
  const [backgroundColor, setBackgroundColor] = useState("rgba(0, 0, 0, 0)");
  const [time, setTime] = useState(item?.timer?.time || 0);
  const isDebate = item?.activity?.type === 'debate';
  const debateLimits = getActivityTimingLimits(item?.activity);
  const timerItem = debateLimits ? { ...item, agendaEvent: { ...item.agendaEvent, ...debateLimits } } : item;
  const [segmentColors, setSegmentColors] = useState({ background: 'rgba(0, 0, 0, 0)', foreground: 'var(--foreground)' });
  const mainTimerColors = getTimerColors(time, timerItem?.agendaEvent, {
    background: 'rgba(0, 0, 0, 0)',
    foreground: 'var(--foreground)',
  });
  const timerColors = isDebate ? segmentColors : mainTimerColors;

  // console.log("Regular item", item);

  const timerRef = useRef();

  const { user: authUser } = useSelector(x => x.auth)
  const meetingSpecialAssignments = meeting?.meetingDetails?.specialAssignments || meeting?.specialAssignments || {}
  const authIdentityIds = [
    authUser?.user?.localUserId,
    authUser?.user?._id,
    authUser?.user?.id,
    authUser?.user?.tmAuthUserId,
  ]
    .map(value => String(value || '').trim())
    .filter(Boolean)
  const isTimerManager = Boolean(
    authIdentityIds.length
    && authIdentityIds.includes(String(meetingSpecialAssignments.timerManager || '').trim())
  )
  const isOfficer = authUser?.user?.acl === 'Officer'
  const isMeetingLocked = Boolean(meeting?.meetingDetails?.isLocked)
  const isAssignedPresenter = Boolean(
    authUser?.user?._id && item?.assignedUser?.id && authUser.user._id === item.assignedUser.id
  )
  const canEditRegularItem = Boolean(authUser?.token && (isOfficer || isAssignedPresenter))
  const emitTimerState = (nextIsPlaying) => {
    getSocket().emit('meeting:timer-state', {
      meetingId: meeting?.meetingDetails?.id || meeting?._id || item?.meeting,
      agendaItemId: item?.agendaItemId,
      isPlaying: nextIsPlaying,
      time,
    });
  };

  // console.log("Regular authUser", authUser);

  // Reset time and stop playing when agenda item changes
  useEffect(() => {
    setTime(item?.timer?.time || 0);
  }, [item?.agendaItemId, item?.timer?.time]);
  // Ticks arrive from the server (single source of truth), so no local interval
  // increments/emits here — that would double-count seconds across browsers.

  useEffect(() => {
    const handleExternalTimerReset = (event) => {
      const resetAgendaItemId = String(event?.detail?.agendaItemId || '').trim();
      const currentAgendaItemId = String(item?.agendaItemId || '').trim();
      if (!resetAgendaItemId || resetAgendaItemId !== currentAgendaItemId) return;

      setIsPlaying(false);
      setTime(0);
      setBackgroundColor("rgba(0, 0, 0, 0)");
    };

    window.addEventListener('meeting:timer-reset', handleExternalTimerReset);
    return () => window.removeEventListener('meeting:timer-reset', handleExternalTimerReset);
  }, [item?.agendaItemId, setIsPlaying]);

  useEffect(() => {
    const meetingId = meeting?.meetingDetails?.id || meeting?._id || item?.meeting;
    const agendaItemId = String(item?.agendaItemId || '').trim();
    if (!meetingId || !agendaItemId) return undefined;
    const socket = getSocket();
    const handleTimerTick = (payload) => {
      if (payload?.segmentId || String(payload?.meetingId || '') !== String(meetingId) || String(payload?.agendaItemId || '') !== agendaItemId) return;
      const nextTime = Number(payload?.time);
      if (Number.isFinite(nextTime)) setTime(Math.max(0, nextTime));
    };
    const handleTimerState = (payload) => {
      if (payload?.segmentId || String(payload?.meetingId || '') !== String(meetingId) || String(payload?.agendaItemId || '') !== agendaItemId) return;
      const nextTime = Number(payload?.time);
      if (Number.isFinite(nextTime)) setTime(Math.max(0, nextTime));
      if (typeof payload?.isPlaying === 'boolean') setIsPlaying(payload.isPlaying);
    };
    const handleTimerReset = (payload) => {
      const resetMeetingId = payload?.meetingId || payload?.detail?.meetingId || meetingId;
      const resetAgendaItemId = payload?.agendaItemId || payload?.detail?.agendaItemId;
      if (!payload?.segmentId && !payload?.detail?.segmentId && String(resetMeetingId) === String(meetingId) && String(resetAgendaItemId || '') === agendaItemId) {
        setIsPlaying(false);
        setTime(0);
      }
    };
    socket.on('meeting:timer-tick', handleTimerTick);
    socket.on('meeting:timer-state', handleTimerState);
    socket.on('meeting:timer-reset', handleTimerReset);
    window.addEventListener('meeting:timer-reset', handleTimerReset);
    socket.emit('meeting:join', String(meetingId));
    return () => {
      socket.off('meeting:timer-tick', handleTimerTick);
      socket.off('meeting:timer-state', handleTimerState);
      socket.off('meeting:timer-reset', handleTimerReset);
      window.removeEventListener('meeting:timer-reset', handleTimerReset);
    };
  }, [item?.agendaItemId, item?.meeting, meeting, setIsPlaying]);

  return (
    <Modal.Body
      style={{
        backgroundImage: 'var(--modal-bg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
      }}
      className='timer-header'
      {...(swipeHandlers || {})}
    >
      <Card
        className="text-center mt-0 mb-0 modal-event-card-shell"
        aria-label={isDebate ? 'Debate segment display' : undefined}
        style={{
          width: '100%',
          maxWidth: '100%',
          minHeight: '100%',
          height: '100%',
          margin: 0,
          border: '1px solid rgba(128, 128, 128, 0.35)',
          borderRadius: '12px',
          backgroundColor: !timerDisplayEnabled || timerColors.background === 'rgba(0, 0, 0, 0)'
            ? 'rgba(128, 128, 128, 0.08)'
            : timerColors.background,
          backdropFilter: 'blur(4px)',
          boxShadow: 'none',
          color: timerDisplayEnabled ? timerColors.foreground : 'var(--foreground)',
          cursor: 'auto'
        }}
      >
        <Card.Body style={{ padding: 0, height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
          <div className="next-meeting-content" style={{ height: '100%' }}>
            {!isDebate && <Role item={item} />}
            {!isDebate && (authUser || remoteControlled) &&
              <Limits item={item} canEdit={canEditRegularItem} />
            }
            <Event item={item} />
            {!isDebate && <Row className="justify-content-center align-items-center ">
              <Presenter
                item={item}
                getMeeting={getMeeting}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
                time={time}
                setTime={setTime}
                handleStop={typeof timerRef.current?.handleStop === "function" ? timerRef.current.handleStop : undefined}
                isLocked={isMeetingLocked}
              />
              {(authUser || remoteControlled) && showClockText && timerDisplayEnabled &&
                <Col>
                  <Row>
                    {showClockText && <Clock time={time} isPlaying={isPlaying} />}
                    <Bell backgroundColor={backgroundColor} />

                  </Row>
                </Col>
              }
            </Row>}
            {isDebate && (authUser || remoteControlled) && showClockText && timerDisplayEnabled &&
              <PathwayActivity
                item={item}
                meeting={meeting}
                getMeeting={getMeeting}
                canEdit={canEditRegularItem || isTimerManager}
                isTimerManager={isTimerManager}
                isLocked={isMeetingLocked}
                onSegmentColorsChange={setSegmentColors}
              />
            }
            {authUser && isTimerManager && allowTimerControls &&
              <Timer
                ref={timerRef}
                controlsTarget={timerControlsTarget}
                item={timerItem}
                setBackgroundColor={setBackgroundColor}
                setIsPlaying={setIsPlaying}
                isPlaying={isPlaying}
                getMeeting={getMeeting}
                setTime={setTime}
                time={time}
                isTimerManager={isTimerManager}
                onTimerStateChange={emitTimerState}
                isLocked={isMeetingLocked}
              />
            }
          </div>
        </Card.Body>
      </Card>
    </Modal.Body>
  );
}